// SetupScreen — password creation form. Shown when `!isConfigured`.

import { useState, useRef, useEffect, type FormEvent } from 'react';
import { useAppLock } from './AppLockContext';

const MIN_PASSWORD_LENGTH = 6;

export function SetupScreen() {
  const { lock, skipEncryption } = useAppLock();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!lock) return;
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await lock.setup(password);
      // The `setup` event flips isConfigured; AppGate then renders the app.
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="applock-screen" role="dialog" aria-modal="true">
      <form className="applock-card" onSubmit={onSubmit}>
        <h1 className="applock-title">Create an App Lock password</h1>
        <p className="applock-warning">
          There is no password recovery. If you forget this password you will
          need to clear stored data to start over.
        </p>
        <div className="form-group">
          <label htmlFor="applock-password">Password</label>
          <input
            id="applock-password"
            ref={firstInputRef}
            type="password"
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            disabled={busy}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="applock-confirm">Confirm password</label>
          <input
            id="applock-confirm"
            type="password"
            className="input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            disabled={busy}
            required
          />
        </div>
        {error && (
          <p className="applock-error" role="alert">
            {error}
          </p>
        )}
        <div className="applock-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || !password || !confirm}
          >
            {busy ? 'Setting up…' : 'Create password'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={skipEncryption}
            disabled={busy}
          >
            Skip
          </button>
        </div>
      </form>
    </div>
  );
}
